import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useAppScopeCatalog, useAppScopeNaming, useAppScopeStore } from '@frontend/scope';
import { TreeNode } from '../tree-checklist-dropdown/tree-checklist.types';
import { getAccessibleLocationIds } from '../utils/get-accessible-location-ids';

type Props = {
  permission: Permission;
  /** When provided, restrict the tree to these location ids (groups with no included child are dropped). */
  locationIds?: string[];
};

type UseOrgLocationTreeReturn = {
  accessibleCount: number;
  isError: boolean;
  isLoading: boolean;
  nodes: TreeNode[];
  allLocationIds: string[];
};

const byLabel = (a: TreeNode, b: TreeNode) => a.label.localeCompare(b.label);

export const useOrgLocationTree = ({ permission, locationIds }: Props): UseOrgLocationTreeReturn => {
  const { selectedOrgId } = useAppScopeStore();
  const { accessibleScopeHierarchy } = useAppScopeCatalog();
  const { getScopeName } = useAppScopeNaming();

  return useMemo(() => {
    const orgHierarchy = accessibleScopeHierarchy[selectedOrgId] ?? {};
    // Optional scoping: restrict the tree to a caller-supplied set of locations (e.g. the manage-all
    // selection). null = no restriction (the full org).
    const scope = locationIds ? new Set(locationIds) : null;
    const allLocationIds = Object.values(orgHierarchy)
      .flat()
      .filter((id) => !scope || scope.has(id));
    const accessibleIds = getAccessibleLocationIds(allLocationIds, permission);

    const nodes: TreeNode[] = Object.entries(orgHierarchy)
      .map(([groupId, groupLocationIds]) => {
        const children: TreeNode[] = groupLocationIds
          .filter((id) => !scope || scope.has(id))
          .map((id) => ({
            id,
            label: getScopeName(id, 'location'),
            selectable: accessibleIds.has(id),
            disabled: !accessibleIds.has(id),
          }))
          .sort(byLabel);

        return {
          id: groupId,
          label: getScopeName(groupId, 'group'),
          selectable: false,
          count: children.length,
          children,
        };
      })
      // When scoped, drop groups left with no in-scope children.
      .filter((group) => !scope || (group.children?.length ?? 0) > 0)
      .sort(byLabel);

    return {
      nodes,
      isLoading: false,
      isError: false,
      accessibleCount: accessibleIds.size,
      allLocationIds,
    };
  }, [selectedOrgId, accessibleScopeHierarchy, getScopeName, permission, locationIds]);
};
